/**
 * useTradeSounds — sound alerts + closed-position detection for Manual Trading.
 *
 * Tracks the SL/TP prices of every position seen, indexed by
 * "<accountId>-<brokerId>". When a position disappears between two polls we
 * compare its LAST recorded close-price to its SL/TP to figure out which was
 * hit → chime + toast.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

export function useTradeSounds(isAr) {
  const knownPositionsRef = useRef(new Map());
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      const v = localStorage.getItem('mt5_manual_sound');
      return v == null ? true : v === '1';
    } catch { return true; }
  });

  const toggleSound = () => {
    setSoundEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem('mt5_manual_sound', next ? '1' : '0'); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  // WebAudio beep — rising tones for TP (win), falling for SL (loss),
  // neutral for other closes, so the trader can HEAR the outcome.
  const playChime = useCallback((kind /* 'tp' | 'sl' | 'closed' */) => {
    if (!soundEnabled) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTime;
      const tones = kind === 'tp'
        ? [523.25, 783.99]              // C5 → G5 (rising = win)
        : kind === 'sl'
          ? [523.25, 349.23]            // C5 → F4 (falling = loss)
          : [659.25, 659.25];           // E5 twice (neutral close)
      tones.forEach((f, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = f;
        const start = now + i * 0.13;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.15, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.22);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.25);
      });
      setTimeout(() => { try { ctx.close(); } catch (_e) { /* ignore */ } }, 800);
    } catch (_e) {
      /* silent - browser may block AudioContext without user gesture */
    }
  }, [soundEnabled]);

  // Diff the new positions snapshot against the last-known map — toast+chime
  // for any position that vanished (TP hit / SL hit / manual close).
  const trackAndNotify = useCallback((next) => {
    const currentKeys = new Set();
    const currentMap = new Map();
    (next.accounts || []).forEach((accBlock) => {
      (accBlock.positions || []).forEach((p) => {
        const key = `${accBlock.account_id}-${p.broker_id}`;
        currentKeys.add(key);
        currentMap.set(key, {
          symbol: p.symbol,
          side: p.type,
          volume: p.volume,
          sl: p.sl,
          tp: p.tp,
          current_price: p.current_price ?? p.open_price,
          profit: p.profit,
          accountId: accBlock.account_id,
        });
      });
    });

    knownPositionsRef.current.forEach((prev, key) => {
      if (!currentKeys.has(key)) {
        // Only claim "SL/TP hit" when the last observed price is genuinely
        // close (< 0.2%) to the target; otherwise it's almost certainly a
        // manual close (or broker stop-out) → neutral notification.
        const price = prev.current_price;
        let kind = 'closed';
        const withinPct = (a, b) => a != null && b != null && Math.abs(a - b) / Math.max(1e-9, Math.abs(b)) < 0.002;
        if (prev.tp && withinPct(price, prev.tp)) kind = 'tp';
        else if (prev.sl && withinPct(price, prev.sl)) kind = 'sl';

        const label = kind === 'tp'
          ? (isAr ? `🎯 تم لمس TP على ${prev.symbol}` : `🎯 TP hit on ${prev.symbol}`)
          : kind === 'sl'
            ? (isAr ? `🛑 تم لمس SL على ${prev.symbol}` : `🛑 SL hit on ${prev.symbol}`)
            : (isAr ? `صفقة ${prev.symbol} أُغلقت` : `${prev.symbol} position closed`);
        if (kind === 'tp') toast.success(label, { duration: 8000 });
        else if (kind === 'sl') toast.error(label, { duration: 8000 });
        else toast.info?.(label) || toast(label);
        playChime(kind);
      }
    });

    knownPositionsRef.current = currentMap;
  }, [isAr, playChime]);

  return { soundEnabled, toggleSound, knownPositionsRef, trackAndNotify };
}
